/**
 * Precious Metals IRA Interactive Components
 * Namespaced with ab- prefix
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    initTabs();
    initAccordions();
    initRolloverHelper();
    initEligibilityChecker();
    initLeadForms();
    initMobileCTA();
  }

  // Tab functionality
  function initTabs() {
    const tabButtons = document.querySelectorAll('.ab-tab-btn');
    const tabPanels = document.querySelectorAll('.ab-tab-panel');

    tabButtons.forEach(button => {
      button.addEventListener('click', () => {
        const targetId = button.getAttribute('aria-controls');
        const targetPanel = document.getElementById(targetId);

        // Hide all panels
        tabPanels.forEach(panel => {
          panel.hidden = true;
          panel.classList.remove('ab-active');
        });

        // Deactivate all buttons
        tabButtons.forEach(btn => {
          btn.classList.remove('ab-active');
          btn.setAttribute('aria-selected', 'false');
        });

        // Show target panel
        if (targetPanel) {
          targetPanel.hidden = false;
          targetPanel.classList.add('ab-active');
          button.classList.add('ab-active');
          button.setAttribute('aria-selected', 'true');
        }
      });
    });
  }

  // Accordion functionality
  function initAccordions() {
    const accordionButtons = document.querySelectorAll('.ab-faq-question');

    accordionButtons.forEach(button => {
      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') === 'true';
        const targetId = button.getAttribute('aria-controls');
        const targetPanel = document.getElementById(targetId);

        if (targetPanel) {
          button.setAttribute('aria-expanded', !expanded);
          targetPanel.hidden = expanded;
        }
      });
    });
  }

  // Rollover vs Transfer Helper
  function initRolloverHelper() {
    const helperForm = document.querySelector('[data-form-type="rollover-helper"]');
    if (!helperForm) return;

    const calculateBtn = helperForm.querySelector('[data-action="calculate-rollover"]');
    const resultDiv = helperForm.querySelector('.ab-helper-result');
    const resultText = helperForm.querySelector('.ab-result-text');

    if (calculateBtn) {
      calculateBtn.addEventListener('click', () => {
        const fromAccount = helperForm.querySelector('input[name="from-account"]:checked');
        const recentRollover = helperForm.querySelector('input[name="recent-rollover"]:checked');

        if (!fromAccount || !recentRollover) {
          resultText.textContent = 'Please answer both questions to get a recommendation.';
          resultDiv.style.display = 'block';
          return;
        }

        let recommendation = '';

        if (recentRollover.value === 'yes') {
          recommendation = 'You should use a direct trustee-to-trustee transfer. Since you\'ve done a rollover in the past 12 months, you cannot do another rollover without tax consequences. Direct transfers have no such limitation.';
        } else if (fromAccount.value === 'ira') {
          recommendation = 'A direct trustee-to-trustee transfer is recommended for IRA-to-IRA moves. It\'s simpler, has no time limits, and avoids any risk of missing deadlines.';
        } else if (fromAccount.value === '401k') {
          recommendation = 'You can use either a direct rollover or a 60-day rollover from your 401(k). A direct rollover is safer as it avoids the 20% withholding and 60-day deadline. If you choose the 60-day option, remember you\'ll need to replace the 20% withheld from other funds.';
        }

        resultText.textContent = recommendation;
        resultDiv.style.display = 'block';
      });
    }
  }

  // Eligibility Checker
  function initEligibilityChecker() {
    const checkerForm = document.querySelector('[data-checker="eligibility"]');
    if (!checkerForm) return;

    const metalSelect = checkerForm.querySelector('#metal-type');
    const productSelect = checkerForm.querySelector('#product-example');
    const checkBtn = checkerForm.querySelector('[data-action="check-eligibility"]');
    const resultDiv = checkerForm.querySelector('.ab-checker-result');
    const resultStatus = checkerForm.querySelector('.ab-result-status');

    const products = {
      gold: [
        { value: 'eagle', text: 'American Gold Eagle', eligible: true },
        { value: 'buffalo', text: 'American Gold Buffalo', eligible: true },
        { value: 'maple', text: 'Canadian Gold Maple Leaf', eligible: true },
        { value: 'krugerrand', text: 'South African Krugerrand', eligible: true },
        { value: 'numismatic', text: 'Pre-1933 Gold Coins', eligible: false },
        { value: 'jewelry', text: 'Gold Jewelry', eligible: false }
      ],
      silver: [
        { value: 'eagle', text: 'American Silver Eagle', eligible: true },
        { value: 'maple', text: 'Canadian Silver Maple Leaf', eligible: true },
        { value: 'britannia', text: 'British Silver Britannia', eligible: true },
        { value: 'morgan', text: 'Morgan Silver Dollar', eligible: false },
        { value: 'junk', text: '90% Junk Silver', eligible: false },
        { value: 'sterling', text: 'Sterling Silver Items', eligible: false }
      ],
      platinum: [
        { value: 'eagle', text: 'American Platinum Eagle', eligible: true },
        { value: 'maple', text: 'Canadian Platinum Maple Leaf', eligible: true },
        { value: 'koala', text: 'Australian Platinum Koala', eligible: true },
        { value: 'jewelry', text: 'Platinum Jewelry', eligible: false }
      ],
      palladium: [
        { value: 'eagle', text: 'American Palladium Eagle', eligible: true },
        { value: 'maple', text: 'Canadian Palladium Maple Leaf', eligible: true },
        { value: 'bars', text: 'PAMP Suisse Palladium Bars', eligible: true },
        { value: 'industrial', text: 'Industrial Palladium', eligible: false }
      ]
    };

    if (metalSelect) {
      metalSelect.addEventListener('change', () => {
        const selectedMetal = metalSelect.value;
        productSelect.innerHTML = '<option value="">Select a product...</option>';
        productSelect.disabled = !selectedMetal;

        if (selectedMetal && products[selectedMetal]) {
          products[selectedMetal].forEach(product => {
            const option = document.createElement('option');
            option.value = product.value;
            option.textContent = product.text;
            option.dataset.eligible = product.eligible;
            productSelect.appendChild(option);
          });
        }
      });
    }

    if (checkBtn) {
      checkBtn.addEventListener('click', () => {
        const selectedProduct = productSelect.options[productSelect.selectedIndex];
        
        if (!selectedProduct || !selectedProduct.value) {
          resultStatus.textContent = 'Please select both a metal type and specific product.';
          resultDiv.style.display = 'block';
          return;
        }

        const isEligible = selectedProduct.dataset.eligible === 'true';
        
        if (isEligible) {
          resultStatus.textContent = `✓ ${selectedProduct.textContent} is typically eligible for precious metals IRAs, meeting IRS fineness and approval requirements.`;
          resultStatus.style.color = 'var(--ab-success)';
        } else {
          resultStatus.textContent = `✗ ${selectedProduct.textContent} is typically not eligible for precious metals IRAs. This may be due to fineness requirements, collectible status, or IRS restrictions.`;
          resultStatus.style.color = 'var(--ab-error)';
        }
        
        resultDiv.style.display = 'block';
      });
    }
  }

  // Lead Form Handling
  function initLeadForms() {
    const leadForms = document.querySelectorAll('.ab-lead-form');

    leadForms.forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const submitBtn = form.querySelector('[type="submit"]');
        const btnText = form.querySelector('.ab-btn-text');
        const btnLoading = form.querySelector('.ab-btn-loading');
        const successMsg = form.querySelector('.ab-form-message.ab-success');
        const errorMsg = form.querySelector('.ab-form-message.ab-error');
        const errorText = form.querySelector('.ab-error-text');

        // Clear previous messages
        if (successMsg) successMsg.style.display = 'none';
        if (errorMsg) errorMsg.style.display = 'none';

        // Validate form
        if (!form.checkValidity()) {
          form.reportValidity();
          return;
        }

        // Show loading state
        if (btnText) btnText.style.display = 'none';
        if (btnLoading) btnLoading.style.display = 'inline';
        if (submitBtn) submitBtn.disabled = true;

        // Gather form data
        const formData = new FormData(form);
        const data = Object.fromEntries(formData);
        const endpoint = form.dataset.endpoint;

        try {
          if (!endpoint || endpoint === '/api/lead-forms/ira-kit' || endpoint === '/api/lead-forms/consultation') {
            // Simulate success for demo (no real endpoint)
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            // Show success
            if (successMsg) successMsg.style.display = 'block';
            form.reset();
          } else {
            // Real endpoint
            const response = await fetch(endpoint, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(data)
            });

            if (!response.ok) {
              throw new Error('Network response was not ok');
            }

            // Show success
            if (successMsg) successMsg.style.display = 'block';
            form.reset();
          }
        } catch (error) {
          // Show error
          if (errorMsg) {
            if (errorText) {
              errorText.textContent = 'Unable to process your request. Please try again or call us directly.';
            }
            errorMsg.style.display = 'block';
          }
        } finally {
          // Reset button state
          if (btnText) btnText.style.display = 'inline';
          if (btnLoading) btnLoading.style.display = 'none';
          if (submitBtn) submitBtn.disabled = false;
        }
      });
    });
  }

  // Mobile Sticky CTA
  function initMobileCTA() {
    const mobileCTA = document.querySelector('.ab-mobile-cta');
    if (!mobileCTA) return;

    let lastScrollY = window.scrollY;
    let ticking = false;

    function updateCTAVisibility() {
      const currentScrollY = window.scrollY;
      
      // Only show on mobile
      if (window.innerWidth > 768) {
        mobileCTA.classList.remove('ab-show');
        return;
      }

      // Show when scrolling up or at top
      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        mobileCTA.classList.add('ab-show');
      } else {
        mobileCTA.classList.remove('ab-show');
      }

      lastScrollY = currentScrollY;
      ticking = false;
    }

    function requestTick() {
      if (!ticking) {
        window.requestAnimationFrame(updateCTAVisibility);
        ticking = true;
      }
    }

    // Initial check
    updateCTAVisibility();

    // Listen for scroll
    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', updateCTAVisibility, { passive: true });

    // Smooth scroll for CTA button
    const ctaButton = mobileCTA.querySelector('.ab-mobile-btn');
    if (ctaButton) {
      ctaButton.addEventListener('click', (e) => {
        e.preventDefault();
        const leadSection = document.querySelector('.ab-ira-lead-gen');
        if (leadSection) {
          leadSection.scrollIntoView({ behavior: 'smooth' });
        }
      });
    }
  }

})();